<template>
  <div v-if="isEnrolled">
    <!-- Compact form, for the header of a membership panel. Auto-renew is a
         property of one membership, and an account can hold several, so the
         full-width banner that used to sit above the form could only ever
         describe whichever membership happened to be loaded. -->
    <span v-if="compact" class="arb-compact">
      <b-badge variant="info" class="arb-badge">Auto-renew</b-badge>
      <span v-if="cardOnFile" class="arb-meta">{{ cardOnFile }}</span>
      <span v-if="nextRenewalDate" class="arb-meta"
        >Renews {{ nextRenewalDate }}</span
      >
      <b-button
        size="sm"
        variant="outline-primary"
        class="arb-manage"
        @click="showModal = true"
      >
        Manage
      </b-button>
    </span>

    <b-alert
      v-if="!compact"
      show
      variant="info"
      class="d-flex flex-wrap justify-content-between align-items-center mb-3"
    >
      <div>
        <strong>This member is already enrolled in auto-renew.</strong>
        <span v-if="nextRenewalDate">
          Next renewal: <strong>{{ nextRenewalDate }}</strong
          >.
        </span>
        <span v-if="cardOnFile">
          Card on file: <strong>{{ cardOnFile }}</strong
          >.
        </span>
      </div>
      <b-button
        size="sm"
        variant="outline-primary"
        class="mt-2 mt-md-0"
        @click="showModal = true"
      >
        Manage Subscription
      </b-button>
    </b-alert>

    <b-modal
      id="manage-subscription-modal"
      v-model="showModal"
      title="Manage Auto-Renew"
      :hide-footer="true"
      centered
    >
      <p><strong>Status:</strong> Active</p>
      <p v-if="nextRenewalDate">
        <strong>Next renewal:</strong> {{ nextRenewalDate }}
      </p>
      <p v-if="cardOnFile"><strong>Card on file:</strong> {{ cardOnFile }}</p>
      <b-alert show variant="warning" class="mt-3">
        Cancelling auto-renew stops future automatic charges. The member keeps
        their current membership through its expiration date. They can re-enroll
        at any time.
      </b-alert>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          class="mr-2"
          :disabled="isCancelling"
          @click="showModal = false"
        >
          Close
        </b-button>
        <b-button
          variant="danger"
          :disabled="isCancelling"
          @click="onCancelClick"
        >
          <b-spinner v-if="isCancelling" small class="mr-2" />
          Cancel Auto-Renew
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { cancelArbs } from "@/services/api/utility";
import { manageSubscriptionSchedule } from "@/services/api/stripe";
import { patchMembership } from "@/services/api/membership";
import { warn, error as logError } from "@/utils/logger";

export default {
  name: "ManageAutoRenewBanner",
  props: {
    isEnrolled: { type: Boolean, required: true },
    // Render as a line of header chrome rather than a full-width alert.
    compact: { type: Boolean, default: false },
    nextRenewalDate: { type: String, default: null },
    cardOnFile: { type: String, default: null },
    membershipSfid: { type: String, default: null },
    existingStripeCustomerId: { type: [String, Object], default: null },
  },
  data() {
    return {
      showModal: false,
      isCancelling: false,
    };
  },
  methods: {
    async onCancelClick() {
      if (!this.membershipSfid) {
        this.$bvToast.toast("No member loaded.", {
          title: "Cannot cancel",
          variant: "warning",
          autoHideDelay: 4000,
        });
        return;
      }

      this.isCancelling = true;
      try {
        await cancelArbs(this.membershipSfid);

        if (this.existingStripeCustomerId) {
          try {
            await manageSubscriptionSchedule({
              customer_id:
                typeof this.existingStripeCustomerId === "object"
                  ? this.existingStripeCustomerId.id
                  : this.existingStripeCustomerId,
              membership_sfid: this.membershipSfid,
              auto_renew: false,
            });
          } catch (scheduleErr) {
            warn(
              "cancel-arbs succeeded but Stripe schedule cancellation failed:",
              scheduleErr
            );
          }
        }

        await patchMembership({
          sfid: this.membershipSfid,
          auto_renew__c: false,
        });

        this.showModal = false;
        this.$emit("cancelled");

        this.$bvToast.toast(
          "Auto-renew has been cancelled. The member's current membership remains active until expiration.",
          {
            title: "Auto-renew cancelled",
            variant: "success",
            autoHideDelay: 6000,
          }
        );
      } catch (err) {
        logError("ManageAutoRenewBanner cancel failed:", err);
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Something went wrong while cancelling auto-renew.";
        this.$bvToast.toast(message, {
          title: "Failed to cancel auto-renew",
          variant: "danger",
          autoHideDelay: 8000,
        });
      } finally {
        this.isCancelling = false;
      }
    },
  },
};
</script>

<style scoped>
/* The template compiler condenses the whitespace between these tags away, so
   the badge, the card, the date and the button ran together as one string.
   The spacing has to come from the layout, not from the markup. */
.arb-compact {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.arb-badge {
  font-weight: 600;
}

.arb-meta {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.arb-manage {
  padding: 0.15rem 0.6rem;
  font-size: 0.8rem;
  line-height: 1.4;
}
</style>
